/**
 * <TutorApplicationIntroPage> — un champion présente l'étape qui commence (page).
 *
 * L'ancien `ChampionIntroScreen` (route `intro`) : Abou à la toute première arrivée, puis avant chaque
 * étape d'entretien Tina, Zoé ou Sam, avec les phrases de leur étape (`championsConstants`). Il dit une
 * phrase à la fois ; « Continuer » passe à la suivante, et la dernière ouvre l'étape.
 *
 * Le champion en pied et ce qu'il dit dans sa bulle, comme l'ancien écran
 * (`<TutorApplicationChampionTemplate>`). La bulle reste, la phrase entre à chaque « Continuer » ; des
 * points disent où l'on en est des phrases. Pas d'en-tête : l'intro n'a ni retour ni croix aujourd'hui.
 *
 * Contrôlée : la phrase montrée (`index`) arrive en props, « Continuer » remonte.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { TutorApplicationChampionTemplate } from "../../templates/TutorApplicationChampionTemplate"

const TUTOR_APPLICATION_INTRO_TEXTS = { next: "Continuer" }

const FIGURE_HEIGHT = { narrow: 300, wide: 420 }

export type TutorApplicationIntroPageProps = {
    champion: ChampionName
    /** Ses phrases, dans l'ordre. */
    texts: readonly string[]
    /** La phrase montrée, à partir de 0. */
    index: number
    onNext: () => void
    /** La dernière phrase ouvre l'étape : le bouton tourne le temps qu'elle s'ouvre. */
    loading?: boolean
    testID?: string
}

export function TutorApplicationIntroPage(props: TutorApplicationIntroPageProps) {
    const { champion, texts, index, onNext, loading = false, testID } = props
    const current = Math.min(Math.max(index, 0), texts.length - 1)

    return (
        <TutorApplicationChampionTemplate
            testID={testID}
            champion={champion}
            figureHeight={FIGURE_HEIGHT}
            text={texts[current] ?? ""}
            stepKey={String(current)}
            below={(wide) =>
                texts.length > 1 && (
                    <Row gap="1" justify={wide ? "start" : "center"} style={styles.dots}>
                        {texts.map((_, i) => (
                            <View key={i} style={styles.dot(i === current, i < current)} />
                        ))}
                    </Row>
                )
            }
            action={<Button label={TUTOR_APPLICATION_INTRO_TEXTS.next} fullWidth loading={loading} onPress={onNext} />}
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    dots: { paddingTop: theme.spacing["1"] },
    dot: (active: boolean, past: boolean) => ({
        width: active ? 20 : 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: active || past ? theme.colors.role : theme.colors.border,
        opacity: past ? 0.45 : 1,
    }),
}))
